/* eslint-disable class-methods-use-this */
import React from 'react';
import BaseClass from 'components/BaseClass';
import { wrapComponent } from 'utils/framework';
import LayoutCard from 'components/FrictionlessCheckout/LayoutCard/LayoutCard';
import AccessPoint from 'components/FrictionlessCheckout/DeliverToReleaseThree/AccessPoint';
import AddressDisplay from 'components/FrictionlessCheckout/DeliverToReleaseThree/AddressDisplay';
import ShipToAccessPoint from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipToAccessPoint';
import SDDInstructions from 'components/FrictionlessCheckout/DeliverToReleaseThree/SDDInstructions';
import FrictionlessCheckoutConstants from 'utils/FrictionlessCheckoutConstants';
import ShipAddressSection from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipAddress/Section';
import { Box } from 'components/ui';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import checkoutUtils from 'utils/Checkout';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import anaConsts from 'analytics/constants';
import { FC_CHECKOUT_PATH, FC_CHECKOUT_SECTIONS, SECTION_NAMES } from 'constants/frictionlessCheckout';
import ShipToAccessPointModal from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipToAccessPoint/ShipToAccessPointModal/ShipToAccessPointModal';
import PickupPersonForm from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipToAccessPoint/PickupPersonForm';
import orderUtils from 'utils/Order';
import addressUtils from 'utils/Address';
import AddressActions from 'actions/AddressActions';
import store from 'store/Store';

const {
    ORDER_TYPES: { GET_IT_SHIPPED }
} = FrictionlessCheckoutConstants;
const { isHalAddress, isPayPalAddress } = orderUtils;

const HAL_ADDRESS = 'HAL';

const ShippingAddressInformation = ({
    isHal,
    address,
    halOperatingHours,
    isHalAvailable,
    children,
    setIsEdit,
    holdAtLocation,
    showAccessPoint = true,
    showDisplayAddress = true,
    isEdit,
    shippingGroupId
}) => (
    <Box paddingX={[4, 4, isEdit ? 4 : 5]}>
        {showDisplayAddress && (
            <AddressDisplay
                address={address}
                isHal={isHal}
                halOperatingHours={halOperatingHours}
                holdAtLocation={holdAtLocation}
                isEdit={isEdit}
                shippingGroupId={shippingGroupId}
                callback={setIsEdit}
            >
                {isHalAvailable && showAccessPoint && (
                    <AccessPoint
                        isHal={isHal}
                        accessPointCallback={setIsEdit}
                        isEdit={false}
                    />
                )}
            </AddressDisplay>
        )}
        {children}
    </Box>
);

class DeliverTo extends BaseClass {
    state = {
        isHal: false,
        isEditByChangeLink: false,
        loadingAddress: this.props.isSdd,
        isNewAddressAdded: false,
        showPickupPersonModal: false,
        addressId: null,
        isAddingNewAddress: false,
        halAddressDraft: null
    };

    getSubtitle = () => {
        const { localization, isSdd, isAutoReplenishBasket, isGis } = this.props;
        const { sDDAndGISOrders, gISAndAROrders, sDDAndAROrders, sDDAndGISAndAROrders } = localization;
        let subtitle = null;

        if (isSdd && isGis && isAutoReplenishBasket) {
            subtitle = sDDAndGISAndAROrders;
        } else if (isGis && isAutoReplenishBasket) {
            subtitle = gISAndAROrders;
        } else if (isSdd && isAutoReplenishBasket) {
            subtitle = sDDAndAROrders;
        } else if (isSdd && isGis) {
            subtitle = sDDAndGISOrders;
        }

        return subtitle;
    };

    handlePayPalUnverified = () => this.setEditChangeLink(true, false);

    /**
     * Handle recommended address callback from address validation
     * Updates the selected PayPal address with the recommended address
     */
    handlePayPalRecommended = recommendedAddress => {
        const { shippingGroup, updateShippingAddress, refreshCheckout } = this.props;
        const currentPayPalAddress = shippingGroup?.address;

        if (!currentPayPalAddress || !shippingGroup?.shippingGroupId) {
            return;
        }

        // Extract fields that shouldn't be sent to API, and create sanitized copy
        const { isPOBoxAddress: recommendedIsPOBoxAddress, addressType: recommendedAddressType, ...sanitizedRecommendedAddress } = recommendedAddress;

        // Merge current PayPal address with recommended address.
        // The backend persists the shipping email under `shipEmail`, not `email`, so
        // without explicitly mapping it here the PayPal-provided email is dropped on update and
        // submitOrder later fails with `checkout.shipping.email.invalid`.
        const updatedAddress = Object.assign(
            {},
            currentPayPalAddress,
            sanitizedRecommendedAddress,
            currentPayPalAddress?.email ? { shipEmail: currentPayPalAddress.email } : {}
        );

        // Prepare data for updateShippingAddress API
        const dataToSend = {
            address: updatedAddress,
            isPOBoxAddress: recommendedIsPOBoxAddress,
            addressType: recommendedAddressType || 'PAYPAL',
            isDefaultAddress: false,
            addressValidated: true,
            shippingGroupId: shippingGroup.shippingGroupId
        };

        // Update shipping address with recommended address
        updateShippingAddress(dataToSend)
            .then(() => refreshCheckout()())
            .catch(error => {
                Sephora.logger.error('[PayPal Address Validation] Failed to update shipping address:', error);
            });
    };

    noop = () => {};

    /**
     * Validate PayPal address with SDD-specific logic
     * For SDD orders: Validates selected address only if it's a PayPal address
     * and preferredZipCode matches the selected address postalCode
     */
    validatePayPalAddress = () => {
        const { orderDetails, addressList, isSdd, shippingGroup } = this.props;

        // Guard: Don't validate if orderDetails not available
        if (!orderDetails) {
            return;
        }

        // Get the selected address from shippingGroup
        const selectedAddress = shippingGroup?.address;

        // Guard: Only validate PayPal addresses
        if (!selectedAddress || !isPayPalAddress(selectedAddress)) {
            return;
        }

        // SDD-specific pre-check: Only validate if preferredZipCode matches selected address postalCode
        if (isSdd) {
            const preferredZipCode = store.getState().user?.preferredZipCode;
            const selectedPostalCode = selectedAddress.postalCode;

            // Skip validation if zip codes don't match
            if (!addressUtils.zipCodesMatch(preferredZipCode, selectedPostalCode)) {
                return;
            }
        }

        // Use common PayPal validation flow for both SDD and non-SDD orders
        // Note: Manual testing has confirmed this validation only triggers once per address
        FrictionlessUtils.validatePayPalAddress({
            orderDetails,
            addressList,
            onUnverified: this.handlePayPalUnverified,
            onRecommended: this.handlePayPalRecommended,
            onCancel: this.noop,
            validateAddressFn: AddressActions.validateAddress,
            pageType: anaConsts.PAGE_TYPES.CHECKOUT,
            isReleaseThreeVerifyAddressModal: true
        });
    };

    componentDidMount() {
        this.props.getAddressBook(false).then(data => {
            this.setState({ loadingAddress: false });
            this.props.updateAddressListWithHalAddress(true);

            // Validate PayPal address after address book is loaded
            this.validatePayPalAddress();

            if (!this.props.skipAutoRouting) {
                FrictionlessUtils.updateCheckoutURLBasedOnOrderCompleteness(data?.addressList);
            }
        });

        const isHal = this.props.shippingGroup.address?.addressType === HAL_ADDRESS;
        this.setState({ isHal }, () => {
            if (isHal) {
                this.props.updateHalAddress(this.props.shippingGroup.address);
            }
        });
    }

    isEditView = prevAddressByPreferredZipCode => {
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        const {
            isEditViewFromURL,
            orderHasPhysicalGiftCard,
            giftCardShippingGroup,
            shippingGroup,
            addressByPreferredZipCode,
            isAnotherCheckoutSectionOpen
        } = this.props;
        const { isEditByChangeLink } = this.state;

        return (
            (!prevAddressByPreferredZipCode && prevAddressByPreferredZipCode !== addressByPreferredZipCode && addressByPreferredZipCode > 1) ||
            (isGuestCheckout &&
                !isEditViewFromURL &&
                !isAnotherCheckoutSectionOpen &&
                orderHasPhysicalGiftCard &&
                giftCardShippingGroup?.isComplete &&
                !shippingGroup?.address?.email &&
                !giftCardShippingGroup?.address?.email &&
                !isEditByChangeLink)
        );
    };

    componentDidUpdate(prevProps) {
        const { shippingGroup: prevShippingGroup, addressByPreferredZipCode: prevAddressByPreferredZipCode } = prevProps;
        const { shippingGroup, isEditViewFromURL, giftCardShippingGroup } = this.props;

        // Validate PayPal address when selected address becomes available or changes
        if (shippingGroup?.address && prevShippingGroup?.address?.addressId !== shippingGroup.address.addressId) {
            this.validatePayPalAddress();
        }

        if (prevShippingGroup.address?.addressType !== shippingGroup.address?.addressType) {
            const isHal = shippingGroup.address?.addressType === HAL_ADDRESS;
            this.setState({ isHal, addressId: !isHal ? shippingGroup.address?.addressId : null });
        }

        if (this.isEditView(prevAddressByPreferredZipCode)) {
            this.setEditChangeLink(true, false);
        }

        if (!isEditViewFromURL && isEditViewFromURL !== prevProps.isEditViewFromURL && this.state.isEditByChangeLink) {
            this.setState({
                isEditByChangeLink: false
            });
        }

        // For SDD + GC orders, the GC shipping group may start with no address.
        // When the SDD group has an address, apply it to the GC group so the backend
        // can assign a shipping method (e.g. USPS First Class).
        const gcNeedsAddress = giftCardShippingGroup && !giftCardShippingGroup.address && shippingGroup?.address;
        const gcJustLostAddress = giftCardShippingGroup && prevProps.giftCardShippingGroup?.address && !giftCardShippingGroup.address;

        if (gcNeedsAddress && !gcJustLostAddress) {
            this.props.updateShippingAddress({
                address: shippingGroup.address,
                shippingGroupId: giftCardShippingGroup.shippingGroupId,
                isDefaultAddress: false
            });
        }

        const deliverToJustGotEmail = !prevShippingGroup.address?.email && shippingGroup?.address?.email;
        const gcShippingGroupNeedsEmail = giftCardShippingGroup?.isComplete && !giftCardShippingGroup?.address?.email;
        const shouldPatchGCWithGuestEmail = checkoutUtils.isGuestOrder() && deliverToJustGotEmail && gcShippingGroupNeedsEmail;

        if (shouldPatchGCWithGuestEmail) {
            this.props.updateShippingAddress({
                address: {
                    ...giftCardShippingGroup.address,
                    shipEmail: shippingGroup.address.email
                },
                shippingGroupId: giftCardShippingGroup.shippingGroupId,
                isDefaultAddress: false
            });
        }
    }

    setPickupLocationEdit = (showPickupPersonModal, isEdit = false, halAddressDraft = null) => {
        const setDefault = this.props.addressList?.length === 0 ? false : isEdit;

        this.setState({
            showPickupPersonModal: showPickupPersonModal,
            isEditByChangeLink: setDefault,
            isAddingNewAddress: false,
            halAddressDraft
        });
    };

    setEditChangeLink = (isEditMode, isTriggeredByUserClick = false) => {
        this.setState({ isEditByChangeLink: isEditMode });

        if (!isEditMode) {
            // Notify parent that address section was explicitly closed (saved/cancelled)
            // This clears forced edit mode set for initially unverified addresses
            this.props.onAddressSaved?.();
        }

        if (isEditMode) {
            const isGuestCheckout = checkoutUtils.isGuestOrder();
            isGuestCheckout && this.props.setAddressNeedsConfirmation(true);
            //The line below makes sure we are adding new address when user clicks change and they only have HAL address.
            this.props.addressList?.length === 1 && this.props.addressList[0].addressType === HAL_ADDRESS && this.setIsAddingNewAddress(true);
            this.fireAnalytics(isTriggeredByUserClick);
            this.props.setActiveSection(this.props.sectionName);
            this.props.goToURL({ path: `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.DELIVER_TO.path}` });
        }

        if (!isEditMode && this.state.isHal) {
            this.props.updateAddressListWithHalAddress(true);
        }
    };

    fireAnalytics = isTriggeredByUserClick => {
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        !isGuestCheckout &&
            FrictionlessCheckoutBindings.setChangeLinkAnalytics(
                anaConsts.PAGE_TYPES.SHIPPING,
                isTriggeredByUserClick,
                this.props.addressList?.length > 1
            );
    };

    setHalWhenChangeLink = (isEditMode, _, halAddressDraft = null) => {
        this.setState({ showPickupPersonModal: true, halAddressDraft });
    };

    setSelectedAddressId = addressId => {
        this.setState({ addressId });
    };

    setIsAddingNewAddress = isAddingNewAddress => {
        this.setState({ isAddingNewAddress });
    };

    renderModal = () => {
        const { localization, halOperatingHours, shippingGroup, isEditViewFromURL } = this.props;

        const { address, shippingGroupId } = shippingGroup;

        const { showPickupPersonModal } = this.state;

        const isGuestCheckout = checkoutUtils.isGuestOrder();
        const addressProp = this.state.halAddressDraft ?? address;

        if (addressProp && !addressProp?.firstName) {
            addressProp.firstName = address?.firstName;
            addressProp.lastName = address?.lastName;
        }

        return (
            <>
                {showPickupPersonModal && (
                    <ShipToAccessPointModal
                        onDismiss={this.setPickupLocationEdit}
                        isOpen={showPickupPersonModal}
                        deliverToEditMode={isEditViewFromURL}
                    >
                        <AddressDisplay
                            address={addressProp}
                            isHal
                            halOperatingHours={addressProp?.halOperatingHours ?? halOperatingHours}
                            holdAtLocation={localization.holdAtLocation}
                            callback={this.setPickupLocationEdit}
                            isDraft
                        />
                        <PickupPersonForm
                            shippingGroupId={shippingGroupId}
                            shippingAddress={addressProp}
                            isGuestCheckout={isGuestCheckout}
                            localization={localization}
                            callback={this.setPickupLocationEdit}
                            isEditMode={isEditViewFromURL}
                        />
                    </ShipToAccessPointModal>
                )}
            </>
        );
    };

    renderEditView = () => {
        const {
            isZeroCheckout,
            addressList,
            isHalAvailable,
            isNewUserFlow,
            shippingGroup,
            orderHasPhysicalGiftCard,
            isSdd,
            isCollapsed,
            isEditViewFromURL,
            addressByPreferredZipCode
        } = this.props;

        const { address, isComplete, shippingGroupId, shippingMethod } = shippingGroup;

        const { isHal, isEditByChangeLink } = this.state;
        const isGuestCheckout = checkoutUtils.isGuestOrder();
        const isRadio = !isNewUserFlow && !isGuestCheckout && !this.state.isAddingNewAddress;
        const hasJustHalAddress = isHalAddress(addressList[0]) && addressList.length === 1;

        return (
            <Box paddingX={[4, 4, 5]}>
                <ShipAddressSection
                    isNewUserFlow={isNewUserFlow && !addressList.length}
                    shippingAddress={address}
                    profileAddresses={addressList || []}
                    shippingGroupId={shippingGroupId}
                    isComplete={isComplete}
                    orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                    isZeroCheckout={isZeroCheckout}
                    isGuestCheckout={isGuestCheckout}
                    shippingMethod={shippingMethod}
                    setAccessPoint={this.setEditChangeLink}
                    isHalAvailable={isHalAvailable}
                    isHal={isHal}
                    isSdd={isSdd}
                    addressByPreferredZipCode={addressByPreferredZipCode}
                    isCollapsed={isCollapsed}
                    analyticsCallback={() => {
                        isSdd && !isEditByChangeLink && this.fireAnalytics();
                    }}
                    onAddressSelect={this.setSelectedAddressId}
                    setIsAddingNewAddress={this.setIsAddingNewAddress}
                    showTaxExemptAddress
                    accessPointCallback={!isHal ? this.setHalWhenChangeLink : this.setPickupLocationEdit}
                    showAddressFormForHalGuestCheckout={isHal && (isGuestCheckout || hasJustHalAddress) && isEditByChangeLink}
                    currentAddressListLength={addressList?.length || 0}
                    sectionName={SECTION_NAMES.DELIVER_TO}
                    isEditView={isEditViewFromURL}
                    onAddressSaved={this.props.onAddressSaved}
                >
                    {isHalAvailable && (
                        <AccessPoint
                            isHal={isHal}
                            accessPointCallback={!isHal ? this.setHalWhenChangeLink : this.setPickupLocationEdit}
                            full
                            addressId={this.state.addressId}
                            accessPointAddress={address}
                            isRadio={isRadio}
                            setIsAddingNewAddress={this.setIsAddingNewAddress}
                            isEdit={isEditViewFromURL}
                        />
                    )}
                </ShipAddressSection>
            </Box>
        );
    };

    renderCardBody = () => {
        const {
            localization, isHalAvailable, halOperatingHours, shippingGroup, isEditViewFromURL
        } = this.props;

        const { address, isComplete, shippingGroupId } = shippingGroup;

        const { isHal, isEditByChangeLink } = this.state;
        const isGuestCheckout = checkoutUtils.isGuestOrder();

        return (
            <>
                {this.renderModal()}
                {address && isComplete && (
                    <ShippingAddressInformation
                        isHal={isHal}
                        address={address}
                        halOperatingHours={halOperatingHours}
                        isHalAvailable={isHalAvailable}
                        setIsEdit={this.setPickupLocationEdit}
                        showAccessPoint={!isEditViewFromURL && !isHal}
                        holdAtLocation={localization.holdAtLocation}
                        showDisplayAddress={!isEditViewFromURL}
                        isEdit={isEditViewFromURL || isEditByChangeLink}
                        shippingGroupId={shippingGroupId}
                    >
                        {isHal && !isEditViewFromURL && (
                            <ShipToAccessPoint
                                isComplete={isComplete}
                                shippingAddress={address}
                                editMode={isEditViewFromURL}
                                shippingGroupId={shippingGroupId}
                                callback={this.setPickupLocationEdit}
                                isGuestCheckout={isGuestCheckout}
                                onAddressSaved={this.props.onAddressSaved}
                            />
                        )}
                    </ShippingAddressInformation>
                )}
                {(isEditByChangeLink || isEditViewFromURL) && this.renderEditView()}
            </>
        );
    };

    render() {
        const {
            orderType,
            localization,
            sectionNumber,
            isSdd,
            isNewUserFlow,
            isCollapsed,
            hasSDUOnlyInSddBasket,
            sectionLevelError,
            hasCmsContent,
            isEditViewFromURL
        } = this.props;
        const { loadingAddress, isEditByChangeLink } = this.state;

        let title;

        if (orderType === GET_IT_SHIPPED) {
            title = localization.getItShippedTitle;
        }

        const subTitle = isCollapsed ? null : this.getSubtitle();

        return (
            <LayoutCard
                sectionInfo={{
                    sectionNumber: !sectionLevelError && sectionNumber,
                    title,
                    subTitle,
                    hasDivider: true,
                    isChangePermitted: !isEditViewFromURL,
                    onChangeClick: this.setEditChangeLink,
                    sectionLevelError,
                    sectionName: SECTION_NAMES.DELIVER_TO
                }}
                {...(hasCmsContent && { marginTop: [4, 4, 5] })}
                hasPaddingForChildren={false}
                isEditMode={(isEditViewFromURL || isNewUserFlow) && !isCollapsed}
                buttonCustomStyle={styles}
                isNewUserFlow={isNewUserFlow}
                isCollapsed={isCollapsed}
                ariaLabel={localization.deliveryAndPickupInformation}
                role='region'
            >
                {loadingAddress && isSdd ? (
                    <Box
                        paddingX={[4, 4, 5]}
                        minHeight={133}
                    />
                ) : (
                    <>
                        {this.renderCardBody()}
                        {isSdd && !hasSDUOnlyInSddBasket && !isEditViewFromURL && !isEditByChangeLink && <SDDInstructions />}
                    </>
                )}
            </LayoutCard>
        );
    }
}

const styles = {
    button: {
        width: '48%'
    }
};

export default wrapComponent(DeliverTo, 'DeliverTo', true);
